import { Link } from "react-router-dom";
import { ArrowRight, Zap, Sparkles, Bus, UtensilsCrossed, ShoppingCart, Route, ShieldCheck, FileText, Percent, CreditCard, AlertTriangle, ExternalLink } from "lucide-react";
import Reveal from "../components/Reveal";
import usePageMeta from "../hooks/usePageMeta";

const URL_LEI_VT = "https://www.planalto.gov.br/ccivil_03/leis/l7418.htm";

const PONTOS_LEI = [
    {
        icon: Route,
        titulo: "Abrangência",
        texto: "O vale-transporte é destinado ao deslocamento do trabalhador entre sua residência e o local de trabalho, utilizando o sistema de transporte coletivo público, conforme as condições previstas na legislação.",
    },
    {
        icon: ShieldCheck,
        titulo: "Obrigatoriedade",
        texto: "O empregador pode ficar dispensado do fornecimento do vale-transporte quando proporcionar, por meios próprios ou contratados, o deslocamento residência–trabalho e trabalho–residência de seus empregados, nos termos da legislação aplicável.",
    },
    {
        icon: FileText,
        titulo: "Forma de fornecimento",
        texto: "O vale-transporte possui regras específicas quanto à sua concessão e utilização. A empresa deve observar a legislação vigente e as condições estabelecidas para o benefício.",
    },
    {
        icon: Percent,
        titulo: "Custeio",
        texto: "A participação do empregado pode ocorrer mediante desconto de até 6% do salário básico, cabendo ao empregador arcar com a parcela que exceder esse limite, conforme a legislação.",
    },
    {
        icon: CreditCard,
        titulo: "Aquisição",
        texto: "O benefício deve ser disponibilizado de acordo com a necessidade de deslocamento do trabalhador, observando as regras legais aplicáveis à aquisição e utilização dos créditos.",
    },
];

function Eyebrow({ children }) {
    return (
        <span className="inline-flex items-center gap-2 rounded-full border border-azul-100 bg-azul-50 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.16em] text-azul-700">
            <span className="h-1.5 w-1.5 rounded-full bg-laranja-500" />
            {children}
        </span>
    );
}

function SubSol({ icon: Icon, nome, texto }) {
    return (
        <div className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-suave transition-all duration-300 hover:-translate-y-1 hover:border-laranja-200">
            <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-laranja-50">
                <Icon className="h-5 w-5 text-laranja-500" />
            </div>
            <h4 className="mt-4 font-display text-lg font-bold text-tinta">{nome}</h4>
            <p className="mt-1.5 text-sm leading-relaxed text-tinta-medio">{texto}</p>
        </div>
    );
}

function Secao({ id, img, alt, icon: Icon, tag, titulo, texto, children, reverse = false }) {
    return (
        <section id={id} className="scroll-mt-28 bg-white py-14 md:py-20" data-testid={`secao-${id}`}>
            <div className="container-x grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
                <Reveal className={reverse ? "lg:order-2" : ""}>
                    <div className="relative overflow-hidden rounded-[2rem] shadow-card">
                        <img src={img} alt={alt} loading="lazy" className="h-[300px] w-full object-cover md:h-[380px]" />
                        <div className="absolute inset-0 bg-gradient-to-t from-azul-950/25 to-transparent" />
                    </div>
                </Reveal>
                <div className={reverse ? "lg:order-1" : ""}>
                    <Reveal>
                        <Eyebrow>{tag}</Eyebrow>
                    </Reveal>
                    <Reveal delay={0.08}>
                        <h2 className="mt-5 flex items-start gap-3 font-display text-3xl font-extrabold leading-tight tracking-tight text-tinta sm:text-4xl" data-testid={`${id}-title`}>
                            <Icon className="mt-1.5 h-7 w-7 shrink-0 text-azul-500" strokeWidth={1.8} />
                            {titulo}
                        </h2>
                    </Reveal>
                    <Reveal delay={0.14}>
                        <p className="mt-4 text-base leading-relaxed text-tinta-medio sm:text-lg">{texto}</p>
                    </Reveal>
                    {children}
                    <Reveal delay={0.2}>
                        <Link
                            to="/contato"
                            data-testid={`${id}-cta`}
                            className="group mt-8 inline-flex items-center gap-2 rounded-full bg-azul-500 px-7 py-3.5 text-base font-bold text-white shadow-[0_10px_28px_-8px_rgba(13,82,206,0.55)] transition-all duration-200 hover:-translate-y-0.5 hover:bg-azul-600"
                        >
                            Fale com um especialista
                            <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                        </Link>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}

export default function Solucoes() {
    usePageMeta(
        "Soluções | Smart Benefícios",
        "Vale-Transporte com Smart Pix e Smart Libert, Vale-Refeição e Vale-Alimentação em uma única plataforma, com planos a partir de R$ 99/mês."
    );

    return (
        <>
            {/* Hero da página */}
            <section className="relative overflow-hidden bg-azul-50/70 pb-14 pt-[128px] md:pb-20 md:pt-[168px]" data-testid="solucoes-hero">
                <div aria-hidden="true" className="pointer-events-none absolute -top-24 right-[-8%] h-[420px] w-[420px] rounded-full bg-azul-100/70 blur-3xl" />
                <div className="container-x relative z-10 max-w-3xl">
                    <Reveal><Eyebrow>Soluções</Eyebrow></Reveal>
                    <Reveal delay={0.08}>
                        <h1 className="mt-5 font-display text-4xl font-extrabold leading-[1.08] tracking-tight text-tinta sm:text-5xl lg:text-6xl" data-testid="solucoes-hero-title">
                            Soluções para simplificar a gestão de benefícios
                        </h1>
                    </Reveal>
                    <Reveal delay={0.16}>
                        <p className="mt-5 text-base leading-relaxed text-tinta-medio sm:text-lg">
                            Vale-Transporte, Vale-Refeição e Vale-Alimentação em uma única plataforma. Planos a partir de <strong className="text-azul-700">R$ 99/mês</strong>.
                        </p>
                    </Reveal>
                    <Reveal delay={0.22}>
                        <nav className="mt-8 flex flex-wrap gap-2.5" aria-label="Ir para solução" data-testid="solucoes-ancoras">
                            {[
                                { href: "#vale-transporte", label: "Vale-Transporte" },
                                { href: "#vale-refeicao", label: "Vale-Refeição" },
                                { href: "#vale-alimentacao", label: "Vale-Alimentação" },
                            ].map((a) => (
                                <a
                                    key={a.href}
                                    href={a.href}
                                    data-testid={`solucoes-ancora-${a.href.slice(1)}`}
                                    className="rounded-full border border-azul-200 bg-white px-5 py-2 text-sm font-semibold text-azul-700 transition-all duration-200 hover:-translate-y-0.5 hover:border-azul-500"
                                >
                                    {a.label}
                                </a>
                            ))}
                        </nav>
                    </Reveal>
                </div>
            </section>

            <div className="divide-y divide-slate-100">
                {/* ===== VALE-TRANSPORTE (legislação incluída dentro da aba) ===== */}
                <section id="vale-transporte" className="scroll-mt-28 bg-white py-14 md:py-20" data-testid="secao-vale-transporte">
                    <div className="container-x grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
                        <Reveal>
                            <div className="relative overflow-hidden rounded-[2rem] shadow-card">
                                <img
                                    src="https://static.prod-images.emergentagent.com/jobs/6aa031ac-bbd2-48ac-9d32-a39422d666e7/images/7c635e85fc4810fd02b463813bd9098381c1cb2c5ec9ca75ee9737e2e3907b6a.jpeg"
                                    alt="Pessoa passando pela catraca de um ônibus urbano brasileiro com cartão de transporte amarelo"
                                    loading="lazy"
                                    className="h-[300px] w-full object-cover md:h-[380px]"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-azul-950/25 to-transparent" />
                            </div>
                        </Reveal>
                        <div>
                            <Reveal><Eyebrow>Vale-Transporte</Eyebrow></Reveal>
                            <Reveal delay={0.08}>
                                <h2 className="mt-5 flex items-start gap-3 font-display text-3xl font-extrabold leading-tight tracking-tight text-tinta sm:text-4xl" data-testid="vale-transporte-title">
                                    <Bus className="mt-1.5 h-7 w-7 shrink-0 text-azul-500" strokeWidth={1.8} />
                                    Uma solução completa para facilitar a gestão do transporte dos colaboradores, com mais praticidade e controle para o RH.
                                </h2>
                            </Reveal>
                            <Reveal delay={0.14}>
                                <p className="mt-4 text-base leading-relaxed text-tinta-medio sm:text-lg">
                                    Dentro da solução de Vale-Transporte, a Smart oferece recursos que tornam a disponibilização e a utilização do benefício mais simples para empresa e colaborador.
                                </p>
                            </Reveal>
                            <div className="mt-8 grid gap-4 sm:grid-cols-2">
                                <SubSol icon={Zap} nome="Smart Pix" texto="Uma alternativa prática para disponibilização do benefício através do Pix." />
                                <SubSol icon={Sparkles} nome="Smart Libert" texto="Mais liberdade e flexibilidade para a gestão e utilização do benefício de transporte." />
                            </div>
                            <Reveal delay={0.2}>
                                <Link
                                    to="/contato"
                                    data-testid="vale-transporte-cta"
                                    className="group mt-8 inline-flex items-center gap-2 rounded-full bg-azul-500 px-7 py-3.5 text-base font-bold text-white shadow-[0_10px_28px_-8px_rgba(13,82,206,0.55)] transition-all duration-200 hover:-translate-y-0.5 hover:bg-azul-600"
                                >
                                    Fale com um especialista
                                    <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                                </Link>
                            </Reveal>
                        </div>
                    </div>

                    {/* Legislação — conteúdo do Vale-Transporte */}
                    <div className="container-x">
                        <Reveal>
                            <div className="mx-auto mt-14 max-w-5xl rounded-[2rem] border border-azul-100 bg-azul-50/60 p-7 md:p-12" data-testid="legislacao-vt-panel">
                                <Eyebrow>Legislação</Eyebrow>
                                <h3 className="mt-5 font-display text-2xl font-extrabold tracking-tight text-tinta sm:text-3xl" data-testid="legislacao-title">
                                    Entenda o Vale-Transporte
                                </h3>
                                <h4 className="mt-6 font-display text-lg font-bold text-azul-900" data-testid="legislacao-subtitulo">Lei do Vale-Transporte</h4>
                                <p className="mt-2 max-w-3xl text-[15px] leading-relaxed text-tinta-medio" data-testid="legislacao-leis">
                                    O direito ao vale-transporte é assegurado pela <strong className="text-azul-700">Lei nº 7.418, de 16 de dezembro de 1985</strong>, posteriormente alterada pela <strong className="text-azul-700">Lei nº 7.619, de 30 de setembro de 1987</strong>, e regulamentada pelo <strong className="text-azul-700">Decreto nº 95.247/1987</strong>.
                                </p>

                                <div className="mt-8 grid gap-4 md:grid-cols-2" data-testid="legislacao-pontos">
                                    {PONTOS_LEI.map((p, i) => (
                                        <Reveal key={p.titulo} delay={i * 0.06} className="h-full">
                                            <div className="h-full rounded-2xl border border-slate-200/80 bg-white p-6 shadow-suave transition-all duration-300 hover:-translate-y-1 hover:shadow-card">
                                                <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-azul-50">
                                                    <p.icon className="h-5 w-5 text-azul-600" strokeWidth={1.8} />
                                                </div>
                                                <h5 className="mt-4 font-display text-lg font-bold text-tinta">{p.titulo}</h5>
                                                <p className="mt-1.5 text-sm leading-relaxed text-tinta-medio">{p.texto}</p>
                                            </div>
                                        </Reveal>
                                    ))}
                                    <Reveal delay={0.36} className="h-full md:col-span-2">
                                        <div className="flex h-full items-start gap-3.5 rounded-2xl border border-laranja-200 bg-laranja-50 p-6">
                                            <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-laranja-100">
                                                <AlertTriangle className="h-5 w-5 text-laranja-600" strokeWidth={1.8} />
                                            </span>
                                            <div>
                                                <h5 className="font-display text-lg font-bold text-tinta">Importante</h5>
                                                <p className="mt-1.5 text-sm leading-relaxed text-tinta-medio">
                                                    As informações apresentadas têm caráter informativo e não substituem a legislação vigente nem uma orientação jurídica especializada.
                                                </p>
                                            </div>
                                        </div>
                                    </Reveal>
                                </div>

                                <Reveal delay={0.1}>
                                    <div className="mt-9 text-center">
                                        <a
                                            href={URL_LEI_VT}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            data-testid="legislacao-btn"
                                            className="group inline-flex items-center gap-2 rounded-full bg-azul-500 px-7 py-3.5 text-base font-bold text-white shadow-[0_10px_28px_-8px_rgba(13,82,206,0.55)] transition-all duration-200 hover:-translate-y-0.5 hover:bg-azul-600"
                                        >
                                            Conheça a legislação completa
                                            <ExternalLink className="h-4 w-4" />
                                        </a>
                                        <p className="mt-3 text-xs text-tinta-suave">Texto oficial da Lei nº 7.418/1985 — Planalto, Governo Federal.</p>
                                    </div>
                                </Reveal>
                            </div>
                        </Reveal>
                    </div>
                </section>

                <Secao
                    id="vale-refeicao"
                    reverse
                    img="https://images.unsplash.com/photo-1559136646-8d0d1f46a146?q=80&w=1600&auto=format&fit=crop"
                    alt="Pessoas fazendo uma refeição"
                    icon={UtensilsCrossed}
                    tag="Vale-Refeição"
                    titulo="Alimentação com praticidade, controle e eficiência"
                    texto="Ofereça aos seus colaboradores mais praticidade no dia a dia, enquanto sua empresa mantém uma gestão simples e organizada do benefício."
                >
                    <ul className="mt-6 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                        {["Gestão simplificada", "Controle por colaborador", "Relatórios e histórico", "Mais eficiência para o RH"].map((d) => (
                            <li key={d} className="flex items-center gap-2.5 rounded-xl border border-slate-200/80 bg-white px-4 py-3 text-sm font-semibold text-tinta-medio shadow-suave">
                                <span className="h-2 w-2 shrink-0 rounded-full bg-laranja-500" />
                                {d}
                            </li>
                        ))}
                    </ul>
                </Secao>

                <Secao
                    id="vale-alimentacao"
                    img="https://images.unsplash.com/photo-1685640206182-c51b8aa9b686?q=80&w=1600&auto=format&fit=crop"
                    alt="Carrinho de compras de supermercado com alimentos"
                    icon={ShoppingCart}
                    tag="Vale-Alimentação"
                    titulo="Mais liberdade para o colaborador. Mais controle para a empresa."
                    texto="Ofereça um benefício essencial para seus colaboradores com uma gestão moderna e eficiente."
                >
                    <ul className="mt-6 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                        {["Gestão centralizada", "Controle de colaboradores", "Mais praticidade para o RH", "Segurança e eficiência"].map((d) => (
                            <li key={d} className="flex items-center gap-2.5 rounded-xl border border-slate-200/80 bg-white px-4 py-3 text-sm font-semibold text-tinta-medio shadow-suave">
                                <span className="h-2 w-2 shrink-0 rounded-full bg-laranja-500" />
                                {d}
                            </li>
                        ))}
                    </ul>
                </Secao>
            </div>

            {/* Faixa de reforço da mensagem */}
            <section className="bg-azul-50/70 py-16 md:py-20" data-testid="solucoes-reforco">
                <div className="container-x text-center">
                    <Reveal>
                        <p className="mx-auto max-w-3xl font-display text-2xl font-extrabold leading-snug tracking-tight text-azul-900 sm:text-3xl">
                            Todas as soluções em uma única plataforma — <span className="text-laranja-500">planos a partir de R$ 99/mês</span>.
                        </p>
                    </Reveal>
                </div>
            </section>
        </>
    );
}
